/**
 * Event Bridge for Host App Communication
 *
 * This module provides event-based communication between the host app (jfc-app)
 * and the chat widget using CustomEvent API.
 */

// ============================================
// Event Constants
// ============================================

export const JFC_CHAT_EVENTS = {
    /** Dispatched by host app when user logs in */
    SYNC_USER: 'jfc-chat:sync-user',
    /** Dispatched by host app when user logs out */
    LOGOUT_USER: 'jfc-chat:logout-user',
    /** Dispatched by widget when it's ready to receive events */
    WIDGET_READY: 'jfc-chat:widget-ready',
    /** Dispatched by host app when language changes */
    CHANGE_LANGUAGE: 'jfc-chat:change-language',
    /** Dispatched by widget when membership status changes */
    ON_MEMBER_STATUS: 'jfc-chat:on-member-status'
} as const;

// ============================================
// Payload Types
// ============================================

export interface SyncUserPayload {
    userid: string;
    email: string;
    customerId: string;     // Unique customer ID, used as groupID in chat context
    customerNo: string;     // Customer number (e.g. "10-0000"), for display only
    customerName: string;   // groupName in chat context
    userType: 'customer' | 'sale' | 'salesrep' | 'SalesRep';
    jfcToken: string;       // JFC auth token for API calls
}

export interface LogoutUserPayload {
    userid: string;
    timestamp: number;
}

export interface ChangeLanguagePayload {
    language: string;  // 'en' | 'es' | 'ja' | 'ko' | 'zh'
}

export interface WidgetReadyPayload {
    timestamp: number;
}

export interface MemberStatusPayload {
    isMember: boolean;
    groupID: string | null;
    timestamp: number;
}

// ============================================
// Event Dispatchers (for widget side)
// ============================================

/**
 * Dispatch widget ready event to notify host app that widget is ready
 */
export function dispatchWidgetReady(): void {
    const event = new CustomEvent<WidgetReadyPayload>(JFC_CHAT_EVENTS.WIDGET_READY, {
        detail: {
            timestamp: Date.now()
        },
        bubbles: true,
        composed: true
    });

    window.dispatchEvent(event);
    console.log('[ChatWidget] WIDGET_READY dispatched');
}

/**
 * Dispatch member status event to notify host app of membership changes
 */
export function dispatchMemberStatus(isMember: boolean, groupID: string | null): void {
    const event = new CustomEvent<MemberStatusPayload>(JFC_CHAT_EVENTS.ON_MEMBER_STATUS, {
        detail: {
            isMember,
            groupID,
            timestamp: Date.now()
        },
        bubbles: true,
        composed: true
    });

    window.dispatchEvent(event);
    console.log('[ChatWidget] ON_MEMBER_STATUS dispatched:', { isMember, groupID });
}

// ============================================
// Event Type Guards
// ============================================

export function isSyncUserEvent(event: Event): event is CustomEvent<SyncUserPayload> {
    return event.type === JFC_CHAT_EVENTS.SYNC_USER;
}

export function isLogoutUserEvent(event: Event): event is CustomEvent<LogoutUserPayload> {
    return event.type === JFC_CHAT_EVENTS.LOGOUT_USER;
}

export function isChangeLanguageEvent(event: Event): event is CustomEvent<ChangeLanguagePayload> {
    return event.type === JFC_CHAT_EVENTS.CHANGE_LANGUAGE;
}
